/**
 * Role-Based Access Control (RBAC) Utilities
 * Enforces access control at route and data-action levels
 */

import { COLLECTION_REGISTRY } from '../config/collectionsRegistry';

/**
 * Check if user has any of the allowed roles
 */
export function hasAnyRole(userRoles, allowed) {
  if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) return false;
  const set = new Set(userRoles);
  return allowed.some(r => set.has(r));
}

/**
 * Get minimum role required for write operations based on sensitivity
 */
function minWriteRoleForSensitivity(sensitivity) {
  switch (sensitivity) {
    case 'Critical':
    case 'High':
      return 'admin'; // effectively admin/s_admin only
    case 'Medium':
      return 'developer';
    case 'Low':
    default:
      return 'developer';
  }
}

/**
 * Check if user can access the Developer Dashboard route
 */
export function canAccessRoute(userRoles) {
  return hasAnyRole(userRoles, ['developer', 'admin', 's_admin', 'PlatformEngineer']);
}

/**
 * Check if user can perform a specific action on a collection
 * @param {string[]} userRoles - User's roles
 * @param {string} collectionKey - Collection key from COLLECTION_REGISTRY
 * @param {string} action - Action: 'read' | 'create' | 'update' | 'softDelete' | 'export' | 'bulkUpdate'
 */
export function canPerformActionOnCollection(userRoles, collectionKey, action) {
  const entry = COLLECTION_REGISTRY[collectionKey];
  if (!entry) return false;

  // Read access
  if (action === 'read') {
    return hasAnyRole(userRoles, entry.allowedRoles);
  }

  // Moderators are always read-only if configured
  if (entry.readOnlyFor && entry.readOnlyFor.some(r => userRoles.includes(r))) {
    return false;
  }

  const minRole = minWriteRoleForSensitivity(entry.sensitivity);

  const writeAllowedRoles =
    minRole === 'admin' ? ['admin', 's_admin'] : ['developer', 'admin', 's_admin'];

  // Export and bulk operations are more strict
  if (action === 'export' || action === 'bulkUpdate') {
    return hasAnyRole(userRoles, writeAllowedRoles);
  }

  // Create, update, softDelete
  return hasAnyRole(userRoles, writeAllowedRoles);
}

/**
 * Get user's highest privilege role
 */
export function getHighestRole(userRoles) {
  const roleHierarchy = ['s_admin', 'admin', 'developer', 'moderator'];
  for (const role of roleHierarchy) {
    if (userRoles.includes(role)) return role;
  }
  return null;
}
